import React, { useMemo } from 'react';
import { Loader2, ShieldAlert, Tag } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * Shared inline body for the "merge preview" dialog. Used by both the
 * automatic Duplicate Finder (`FindDuplicates.jsx`) and the manual
 * item-merge flow (`ManualMergeModal.jsx`) so the preview looks and behaves
 * identically in both entry points.
 *
 * Props
 * -----
 * - `preview`: the object returned by `POST /api/items/merge-preview` — must
 *   contain `master` (with `item_id, name, sku, warehouse_qty, vehicle_qty,
 *   category_names`) and `duplicates` (array of the same shape plus
 *   `fk_impacts` and `job_material_count`, `po_line_count`).
 * - `loading`: boolean while the preview request is in flight.
 *
 * This component is intentionally presentational — it does not know about
 * open/close, confirm buttons, or dialog wrappers. Callers wrap it in their
 * own Dialog/Modal.
 */
export function MergePreviewBody({ preview, loading }) {
  const { t } = useTranslation();

  const totalRows = useMemo(() => {
    if (!preview) return 0;
    return (preview.duplicates || []).reduce((sum, d) => {
      const fkRows = (d.fk_impacts || []).reduce((a, b) => a + (b.rows || 0), 0);
      return sum + fkRows;
    }, 0);
  }, [preview]);

  const dupCount = preview ? (preview.duplicates || []).length : 0;

  if (loading) {
    return (
      <div className="py-12 flex items-center justify-center text-slate-500">
        <Loader2 className="h-5 w-5 animate-spin mr-2" />
        {t('duplicates.previewDialog.loading', { defaultValue: 'Building preview…' })}
      </div>
    );
  }

  if (!preview) return null;

  return (
    <div className="space-y-4" data-testid="merge-preview-body">
      <div className="rounded-lg border-2 border-emerald-300 bg-emerald-50 p-3">
        <div className="text-xs font-semibold text-emerald-900 uppercase mb-1">
          {t('duplicates.previewDialog.keepingItem', { defaultValue: 'Keeping this item' })}
        </div>
        <div className="font-medium text-emerald-950">{preview.master.name}</div>
        <div className="text-xs text-emerald-800 mt-1">
          {preview.master.sku && <span>SKU: {preview.master.sku} · </span>}
          {t('duplicates.stat.wh', { defaultValue: 'Warehouse' })}: {preview.master.warehouse_qty} · {t('duplicates.stat.veh', { defaultValue: 'Vehicles' })}: {preview.master.vehicle_qty}
        </div>
        <div className="mt-2" data-testid="merge-preview-master-categories">
          <CategoryRow
            categories={preview.master.category_names}
            testid="merge-preview-master-cat"
          />
        </div>
      </div>

      {Array.isArray(preview.category_warnings) && preview.category_warnings.length > 0 && (
        <div
          className="rounded-lg border-2 border-sky-300 bg-sky-50 p-3 text-sm text-sky-900"
          data-testid="merge-preview-category-warnings"
        >
          <div className="flex items-center gap-1.5 text-xs font-semibold uppercase mb-1">
            <Tag className="h-3.5 w-3.5" />
            Category changes
          </div>
          <ul className="list-disc list-inside space-y-1">
            {preview.category_warnings.map((w, idx) => (
              <li key={idx}>{w}</li>
            ))}
          </ul>
          <div className="mt-1.5 text-[11px] text-sky-700">
            Existing categories on the master are kept; categories that already exist on
            both records are not duplicated.
          </div>
        </div>
      )}

      <div className="space-y-3">
        {(preview.duplicates || []).map((d) => (
          <div key={d.item_id} className="rounded-lg border-2 border-red-200 bg-red-50 p-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <div className="text-xs font-semibold text-red-900 uppercase mb-1">
                  {t('duplicates.previewDialog.duplicateMerged', { defaultValue: 'Merging this item away' })}
                </div>
                <div className="font-medium text-red-950">{d.name}</div>
                <div className="text-xs text-red-800 mt-1">
                  {d.sku && <span>SKU: {d.sku} · </span>}
                  {t('duplicates.stat.wh', { defaultValue: 'Warehouse' })} +{d.warehouse_qty} · {t('duplicates.stat.veh', { defaultValue: 'Vehicles' })} +{d.vehicle_qty} · {t('duplicates.stat.po', { defaultValue: 'PO lines' })}: {d.po_line_count} · {t('duplicates.stat.job', { defaultValue: 'Job uses' })}: {d.job_material_count}
                </div>
                <div className="mt-2" data-testid={`merge-preview-dup-categories-${d.item_id}`}>
                  <CategoryRow
                    categories={d.category_names}
                    testid={`merge-preview-dup-cat-${d.item_id}`}
                  />
                </div>
              </div>
            </div>
            {d.fk_impacts && d.fk_impacts.length > 0 && (
              <details className="mt-2 text-xs">
                <summary className="cursor-pointer text-red-700 select-none">
                  {t('duplicates.previewDialog.tablesRepointed', {
                    count: d.fk_impacts.length,
                    defaultValue: `${d.fk_impacts.length} tables will be repointed`,
                  })}
                </summary>
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-1 font-mono text-[11px] text-red-900">
                  {d.fk_impacts.map((fk) => (
                    <div key={`${fk.table}.${fk.column}`} className="flex justify-between bg-white/60 rounded px-2 py-1">
                      <span>{fk.table}.{fk.column}</span>
                      <span className="font-semibold">{fk.rows}</span>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
        ))}
      </div>

      <div className="rounded-lg bg-slate-100 p-3 text-xs text-slate-700">
        <div className="font-semibold mb-1">
          {t('duplicates.previewDialog.summaryTitle', { defaultValue: 'Summary' })}
        </div>
        <ul className="list-disc list-inside space-y-0.5">
          <li>{t('duplicates.previewDialog.summaryDuplicates', {
            count: dupCount,
            defaultValue: `${dupCount} duplicate(s) will be merged into the master`,
          })}</li>
          <li>{t('duplicates.previewDialog.summaryReferences', {
            count: totalRows,
            defaultValue: `${totalRows} references will be repointed to the master`,
          })}</li>
          <li>{t('duplicates.previewDialog.summaryAudit', {
            defaultValue: 'A permanent audit row is written per merge.',
          })}</li>
        </ul>
      </div>
    </div>
  );
}

export function CategoryRow({ categories, testid }) {
  const list = Array.isArray(categories) ? categories : [];
  if (list.length === 0) {
    return (
      <span
        className="inline-flex items-center gap-1 rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] text-slate-500"
        data-testid={testid ? `${testid}-uncategorized` : 'cat-uncategorized'}
      >
        <Tag className="h-3 w-3" />
        Uncategorized
      </span>
    );
  }
  return (
    <span className="inline-flex flex-wrap items-center gap-1" data-testid={testid}>
      {list.map((c) => (
        <span
          key={c}
          className="inline-flex items-center gap-1 rounded border border-sky-200 bg-sky-50 px-1.5 py-0.5 text-[10px] font-medium text-sky-800"
        >
          <Tag className="h-3 w-3" />
          {c}
        </span>
      ))}
    </span>
  );
}

export function MergePreviewHeaderIcon() {
  return <ShieldAlert className="h-5 w-5 text-amber-500" />;
}

export default MergePreviewBody;
